import type React from 'react'
import { useRef } from 'react'

/** Keys Radix's Slider treats as a discrete step (see `@radix-ui/react-slider`'s own
 *  `ARROW_KEYS`/`PAGE_KEYS`, plus Home/End) — used to tell a genuine keyboard step apart from any
 *  other keydown reaching a Slider's Thumb (e.g. Tab, or Ctrl+Z while it happens to have focus),
 *  which must not be mistaken for one. */
function isSliderStepKey(key: string): boolean {
  return (
    key === 'ArrowUp' ||
    key === 'ArrowDown' ||
    key === 'ArrowLeft' ||
    key === 'ArrowRight' ||
    key === 'PageUp' ||
    key === 'PageDown' ||
    key === 'Home' ||
    key === 'End'
  )
}

/**
 * The Leading/Kerning/Tracking sliders' end-of-gesture handling. `editingRef` is the field's own
 * "input focused" resync guard, which its `onValueChange` sets on every tick; this clears it once
 * the gesture commits, and restores canvas focus after a drag but not after a keyboard step.
 */
export function useSliderCommitGuard(
  editingRef: React.MutableRefObject<boolean>,
  restoreActiveEditor: () => void
) {
  // Set by the Slider's own `onKeyDown` (a real key press), consumed once by its `onValueCommit`
  // right after.
  const keyboardStepRef = useRef(false)

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!isSliderStepKey(e.key)) return

    // Radix itself refocuses the Thumb after applying a keyboard step (inside its
    // own controlled-value `onChange`, which runs before `onValueChange`/
    // `onValueCommit` below ever see it) — precisely so repeated arrow presses keep
    // landing on the slider. `onValueCommit` unconditionally calling
    // `restoreActiveEditor` below would immediately undo that by refocusing the
    // canvas instead, so the very next arrow press moves the text caret rather than
    // the slider — "works once, then stuck". This flag lets `onValueCommit` tell a
    // keyboard step apart from a drag release, where restoring canvas focus is the
    // wanted behavior.
    keyboardStepRef.current = true
  }

  const onValueCommit = () => {
    // For a *controlled* slider, Radix's own
    // `useControllableState` computes `onValueCommit` synchronously INSIDE the
    // value updater it runs before ever notifying `onChange` — so for a discrete
    // keyboard step, `onValueCommit` actually fires BEFORE `onValueChange`, not
    // after (only a drag gesture calls `onValueChange` repeatedly and
    // `onValueCommit` once, at the very end). Clearing the guard and restoring
    // focus here synchronously would therefore race ahead of the very
    // `onValueChange` this commit belongs to. Deferring to a microtask — which
    // always runs after the current synchronous call stack, so after a
    // keyboard step's `onValueChange` has already applied the new value — makes
    // this correct for both gesture types without needing to special-case either.
    queueMicrotask(() => {
      editingRef.current = false

      // A keyboard step: leave focus on the Thumb (Radix already put it back
      // there) so the next arrow press keeps stepping the slider instead of
      // stealing focus to the canvas after every single press.
      if (keyboardStepRef.current) {
        keyboardStepRef.current = false
        return
      }

      restoreActiveEditor()
    })
  }

  return { onKeyDown, onValueCommit }
}
